/* HUENIT LAB 2.0 — Admin: 선생님 등록 요청.
   관리 API(listRequests/resolveRequest) 실연동 — plan.md §5.
   탭: 대기(pending) / 처리됨(approved·rejected). 승인 시 서버가 요청자를 admin(선생님)
   그룹에 편입(§5.1) → 대상 사용자는 재로그인해야 적용(§5.4). */
import { useCallback, useEffect, useState } from 'react';
import { Icon } from '../../icons/Icon.jsx';
import { useToast, Loading, ErrorState, EmptyState } from '../../components/index.js';
import { useSession } from '../../auth/session.jsx';
import { listRequests, resolveRequest } from '../../api/adminApi.js';
import { fmtDateTime } from '../../lib/datetime.js';

const TABS = [
  { value: 'pending', label: '대기', icon: 'bell' },
  { value: 'done',    label: '처리됨', icon: 'check' },
];

function StatusBadge({ status }) {
  if (status === 'approved') return <span className="chip" style={{ background: 'var(--ok-bg)', color: 'var(--ok)' }}><span className="dot" style={{ background: 'var(--ok)' }} />승인됨</span>;
  if (status === 'rejected') return <span className="chip" style={{ background: 'var(--warn-bg)', color: 'var(--warn)' }}><span className="dot" style={{ background: 'var(--warn)' }} />거절됨</span>;
  return <span className="chip" style={{ background: 'var(--brand-50)', color: 'var(--brand-700)' }}><span className="dot" style={{ background: 'var(--brand-600)' }} />대기</span>;
}

export function TeacherRequestsAdmin() {
  const toast = useToast();
  const { getIdToken, logout } = useSession();

  const [tab, setTab] = useState('pending');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);     // {message, status}
  const [busy, setBusy] = useState(null);        // 처리 중인 request_id

  const reload = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      if (tab === 'pending') {
        const { requests } = await listRequests(getIdToken, 'pending');
        setItems(requests);
      } else {
        // 처리됨 = approved + rejected 병합, 시간 역순.
        const [a, r] = await Promise.all([
          listRequests(getIdToken, 'approved'),
          listRequests(getIdToken, 'rejected'),
        ]);
        const merged = [...a.requests, ...r.requests].sort(
          (x, y) => (y.resolved_at || y.created_at || '').localeCompare(x.resolved_at || x.created_at || ''),
        );
        setItems(merged);
      }
    } catch (e) {
      setError({ message: e.message, status: e.status });
    } finally {
      setLoading(false);
    }
  }, [tab, getIdToken]);

  useEffect(() => { reload(); }, [reload]);

  const resolve = async (req, action) => {
    setBusy(req.request_id);
    try {
      await resolveRequest(getIdToken, req.request_id, action);
      setItems(prev => prev.filter(x => x.request_id !== req.request_id));   // 대기 목록에서 제거
      toast(action === 'approve'
        ? '승인했습니다. 요청자가 선생님(admin) 권한을 받았습니다 — 본인 재로그인 후 적용됩니다.'
        : '요청을 거절했습니다.');
    } catch (e) {
      toast(e.message, 'error');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div>
      <div className="toolbar">
        <div className="seg">
          {TABS.map(t => (
            <button key={t.value} type="button" className={"seg__opt" + (tab === t.value ? " is-on" : "")} onClick={() => setTab(t.value)}>
              <Icon name={t.icon} size={15} />{t.label}
            </button>
          ))}
        </div>
        <div style={{ flex: 1 }} />
        <button className="btn btn--ghost btn--sm" onClick={reload} title="새로고침"><Icon name="refresh" size={15} />새로고침</button>
      </div>

      {loading ? (
        <Loading title="요청을 불러오는 중…" />
      ) : error ? (
        <ErrorState error={error} title="요청을 불러오지 못했습니다" onRetry={reload} onRelogin={logout} />
      ) : items.length === 0 ? (
        <EmptyState icon="bell"
          title={tab === 'pending' ? '대기 중인 요청이 없습니다' : '처리된 요청이 없습니다'}
          sub={tab === 'pending' ? '앱에서 새 선생님 등록 요청이 올라오면 여기에 표시됩니다.' : '승인하거나 거절한 요청이 여기에 쌓입니다.'} />
      ) : (
        <div className="rows">
          {items.map(req => {
            const isBusy = busy === req.request_id;
            return (
              <div className="row" key={req.request_id} style={{ alignItems: 'flex-start' }}>
                <div className="row__main">
                  <div className="row__title">
                    {req.nickname || <span style={{ color: 'var(--ink-400)' }}>(닉네임 없음)</span>}
                    <StatusBadge status={req.status} />
                  </div>
                  <div className="row__sub" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <span className="mono">{req.email}</span>
                    <span>요청 {fmtDateTime(req.created_at)}</span>
                    {req.status !== 'pending' && req.resolved_at && (
                      <span>처리 {fmtDateTime(req.resolved_at)}{req.resolved_by ? ` · ${req.resolved_by}` : ''}</span>
                    )}
                  </div>
                  {req.message && (
                    <div style={{ marginTop: 10, padding: '10px 12px', background: 'var(--ink-50)', border: '1px solid var(--ink-150)', borderRadius: 10, fontSize: 13, color: 'var(--ink-700)', lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>
                      {req.message}
                    </div>
                  )}
                </div>
                {req.status === 'pending' && (
                  <div className="row__actions" style={{ paddingTop: 2 }}>
                    <button className="btn btn--soft btn--sm" disabled={isBusy} onClick={() => resolve(req, 'approve')}><Icon name="check" size={15} />승인</button>
                    <button className="btn btn--danger btn--sm" disabled={isBusy} onClick={() => resolve(req, 'reject')}><Icon name="close" size={15} />거절</button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default TeacherRequestsAdmin;
